import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  User,
  Link as LinkIcon,
  Target,
  Briefcase,
  GraduationCap,
  Zap,
  Globe,
  Info,
  Save,
  Plus,
  Trash2,
  Loader2,
  BookOpen,
  FileText,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StateCitySelector } from "@/components/StateCitySelector";
import { DateSelector } from "@/components/DateSelector";
import { LanguageSelector } from "@/components/LanguageSelector";

// Types
interface Experience {
  id?: string;
  company_name: string;
  role: string;
  start_date: string;
  end_date: string | null;
  description: string;
  is_current?: boolean;
}

interface Education {
  id?: string;
  course: string;
  institution: string;
  start_date: string;
  end_date: string | null;
  status: string;
}

interface Course {
  id?: string;
  course_name: string;
  institution: string;
  start_date: string;
  end_date: string | null;
  workload_hours: number | null;
}

interface Language {
  id?: string;
  language: string;
  level: string;
}

interface CandidateProfile {
  id: string;
  full_name: string;
  phone: string | null;
  cpf: string | null;
  city: string | null;
  state: string | null;
  gender: string | null;
  birth_date: string | null;
  bio: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  portfolio_url: string | null;
  personal_website: string | null;
  desired_area: string | null;
  desired_job_type: string | null;
  desired_work_model: string | null;
  availability: string | null;
  salary_expectation_min: number | null;
  salary_expectation_max: number | null;
  is_pcd: boolean;
  soft_skills: string[] | null;
  marital_status: string | null;
  has_children_under_18: boolean | null;
  desired_positions: string[] | null;
}

const AREAS = [
  "Tecnologia da Informação",
  "Administrativo",
  "Comercial / Vendas",
  "Marketing",
  "Recursos Humanos",
  "Financeiro / Contábil",
  "Logística / Operações",
  "Saúde",
  "Educação",
  "Engenharia",
  "Atendimento ao Cliente",
  "Jurídico",
  "Produção / Industrial",
  "Outros",
];

const JOB_TYPES = ["CLT", "Estágio", "PJ", "Freelancer", "Consultor"];
const WORK_MODELS = ["Presencial", "Híbrido", "Remoto"];
const LANGUAGE_LEVELS = ["Básico", "Intermediário", "Avançado", "Fluente"];
const GENDERS = ["Masculino", "Feminino", "Outro", "Prefiro não informar"];
const MARITAL_STATUSES = ["Solteiro(a)", "Casado(a)", "Divorciado(a)", "Viúvo(a)", "União Estável"];
const EDUCATION_STATUSES = [
  { value: "concluido", label: "Concluído" },
  { value: "em_curso", label: "Em curso" },
  { value: "interrompido", label: "Interrompido" },
];

// Mask functions
const formatCPF = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})/, "$1-$2");
};

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};

// Formata número para moeda brasileira (R$ 2.000,00)
const formatCurrency = (value: number | null): string => {
  if (value === null || value === undefined) return "";
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Remove formatação e converte para número
const parseCurrency = (value: string): number | null => {
  if (!value) return null;
  const cleanValue = value
    .replace(/R\$\s?/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? null : parsed;
};

// Handler para máscara de moeda durante digitação
const handleCurrencyInput = (value: string): string => {
  const numbers = value.replace(/\D/g, "");
  if (!numbers) return "";
  const cents = parseInt(numbers, 10);
  const reais = cents / 100;
  return reais.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function CandidatoMinhaContaPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [candidateId, setCandidateId] = useState<string | null>(null);
  const [isFirstAccess, setIsFirstAccess] = useState(false);

  // Profile state
  const [profile, setProfile] = useState<Partial<CandidateProfile>>({
    full_name: "",
    phone: "",
    cpf: "",
    city: "",
    state: "",
    gender: "",
    birth_date: null,
    bio: "",
    linkedin_url: "",
    github_url: "",
    portfolio_url: "",
    personal_website: "",
    desired_area: "",
    desired_job_type: "",
    desired_work_model: "",
    availability: "",
    salary_expectation_min: null,
    salary_expectation_max: null,
    is_pcd: false,
    soft_skills: [],
    marital_status: "",
    has_children_under_18: null,
  });

  // Related data state
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [education, setEducation] = useState<Education[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [hardSkills, setHardSkills] = useState<string[]>([]);
  const [softSkills, setSoftSkills] = useState<string[]>([]);
  const [newHardSkill, setNewHardSkill] = useState("");
  const [newSoftSkill, setNewSoftSkill] = useState("");
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([]);
  const [selectedWorkModels, setSelectedWorkModels] = useState<string[]>([]);
  const [desiredPositions, setDesiredPositions] = useState<string[]>([]);
  const [newPosition, setNewPosition] = useState("");

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const checkAuthAndLoadData = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      toast({
        title: "Acesso negado",
        description: "Você precisa estar logado como candidato para acessar esta página.",
        variant: "destructive",
      });
      navigate("/entrar");
      return;
    }

    setUserEmail(session.user.email || "");

    // Check if user is a candidate
    const { data: candidateData, error } = await supabase
      .from("candidate_profiles")
      .select("*")
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (error || !candidateData) {
      toast({
        title: "Acesso negado",
        description: "Esta página é exclusiva para candidatos.",
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    setCandidateId(candidateData.id);
    setIsFirstAccess(!candidateData.profile_completed);
    setProfile({
      ...candidateData,
      soft_skills: candidateData.soft_skills || [],
    });
    setSoftSkills(candidateData.soft_skills || []);

    if (candidateData.desired_job_type) {
      setSelectedJobTypes(candidateData.desired_job_type.split(",").map((t: string) => t.trim()));
    }
    if (candidateData.desired_work_model) {
      setSelectedWorkModels(candidateData.desired_work_model.split(",").map((m: string) => m.trim()));
    }
    setDesiredPositions(candidateData.desired_positions || []);

    // Load experiences
    const { data: experiencesData } = await supabase
      .from("candidate_experiences")
      .select("*")
      .eq("candidate_id", candidateData.id)
      .order("start_date", { ascending: false });

    if (experiencesData) {
      setExperiences(
        experiencesData.map((e) => ({
          ...e,
          is_current: !e.end_date,
        })),
      );
    }

    // Load skills
    const { data: skillsData } = await supabase
      .from("candidate_skills")
      .select("*")
      .eq("candidate_id", candidateData.id);

    if (skillsData) {
      setHardSkills(skillsData.map((s) => s.skill_name));
    }

    // Load education
    const { data: educationData } = await supabase
      .from("candidate_education")
      .select("*")
      .eq("candidate_id", candidateData.id)
      .order("start_date", { ascending: false });

    if (educationData) {
      setEducation(
        educationData.map((e) => ({
          ...e,
          status: e.status || "concluido",
        })),
      );
    }

    // Load courses
    const { data: coursesData } = await supabase
      .from("candidate_courses")
      .select("*")
      .eq("candidate_id", candidateData.id)
      .order("start_date", { ascending: false });

    if (coursesData) {
      setCourses(coursesData);
    }

    // Load languages
    const { data: languagesData } = await supabase
      .from("candidate_languages")
      .select("*")
      .eq("candidate_id", candidateData.id);

    if (languagesData) {
      setLanguages(languagesData);
    }

    setIsLoading(false);
  };

  const handleSave = async () => {
    if (!candidateId) return;

    setIsSaving(true);

    try {
      // Update profile
      const { error: profileError } = await supabase
        .from("candidate_profiles")
        .update({
          full_name: profile.full_name,
          phone: profile.phone || null,
          city: profile.city || null,
          state: profile.state || null,
          gender: profile.gender || null,
          birth_date: profile.birth_date || null,
          bio: profile.bio || null,
          linkedin_url: profile.linkedin_url || null,
          github_url: profile.github_url || null,
          portfolio_url: profile.portfolio_url || null,
          personal_website: profile.personal_website || null,
          desired_area: profile.desired_area || null,
          desired_job_type: selectedJobTypes.length > 0 ? selectedJobTypes.join(", ") : null,
          desired_work_model: selectedWorkModels.length > 0 ? selectedWorkModels.join(", ") : null,
          availability: profile.availability || null,
          salary_expectation_min: profile.salary_expectation_min || null,
          salary_expectation_max: profile.salary_expectation_max || null,
          is_pcd: profile.is_pcd || false,
          soft_skills: softSkills.length > 0 ? softSkills : null,
          marital_status: profile.marital_status || null,
          has_children_under_18: profile.has_children_under_18,
          desired_positions: desiredPositions.length > 0 ? desiredPositions : null,
        })
        .eq("id", candidateId);

      if (profileError) throw profileError;

      // Mark profile as completed on first save
      if (isFirstAccess) {
        await supabase.from("candidate_profiles").update({ profile_completed: true }).eq("id", candidateId);
        setIsFirstAccess(false);
      }

      // Handle experiences - delete removed, update existing, insert new
      const existingExpIds = experiences.filter((e) => e.id).map((e) => e.id);

      // Delete removed experiences
      await supabase
        .from("candidate_experiences")
        .delete()
        .eq("candidate_id", candidateId)
        .not("id", "in", `(${existingExpIds.join(",") || "00000000-0000-0000-0000-000000000000"})`);

      // Upsert experiences
      for (const exp of experiences) {
        if (exp.id) {
          await supabase
            .from("candidate_experiences")
            .update({
              company_name: exp.company_name,
              role: exp.role,
              start_date: exp.start_date,
              end_date: exp.is_current ? null : exp.end_date,
              description: exp.description,
            })
            .eq("id", exp.id);
        } else {
          await supabase.from("candidate_experiences").insert({
            candidate_id: candidateId,
            company_name: exp.company_name,
            role: exp.role,
            start_date: exp.start_date,
            end_date: exp.is_current ? null : exp.end_date,
            description: exp.description,
          });
        }
      }

      // Handle skills - delete all and re-insert
      await supabase.from("candidate_skills").delete().eq("candidate_id", candidateId);

      if (hardSkills.length > 0) {
        await supabase.from("candidate_skills").insert(
          hardSkills.map((skill) => ({
            candidate_id: candidateId,
            skill_name: skill,
          })),
        );
      }

      // Handle education
      const existingEduIds = education.filter((e) => e.id).map((e) => e.id);

      await supabase
        .from("candidate_education")
        .delete()
        .eq("candidate_id", candidateId)
        .not("id", "in", `(${existingEduIds.join(",") || "00000000-0000-0000-0000-000000000000"})`);

      for (const edu of education) {
        if (edu.id) {
          await supabase
            .from("candidate_education")
            .update({
              course: edu.course,
              institution: edu.institution,
              start_date: edu.start_date,
              end_date: edu.status === "em_curso" ? null : edu.end_date,
              status: edu.status,
            })
            .eq("id", edu.id);
        } else {
          await supabase.from("candidate_education").insert({
            candidate_id: candidateId,
            course: edu.course,
            institution: edu.institution,
            start_date: edu.start_date,
            end_date: edu.status === "em_curso" ? null : edu.end_date,
            status: edu.status,
          });
        }
      }

      // Handle courses
      const existingCourseIds = courses.filter((c) => c.id).map((c) => c.id);

      await supabase
        .from("candidate_courses")
        .delete()
        .eq("candidate_id", candidateId)
        .not("id", "in", `(${existingCourseIds.join(",") || "00000000-0000-0000-0000-000000000000"})`);

      for (const course of courses) {
        if (course.id) {
          await supabase
            .from("candidate_courses")
            .update({
              course_name: course.course_name,
              institution: course.institution,
              start_date: course.start_date,
              end_date: course.end_date,
              workload_hours: course.workload_hours,
            })
            .eq("id", course.id);
        } else {
          await supabase.from("candidate_courses").insert({
            candidate_id: candidateId,
            course_name: course.course_name,
            institution: course.institution,
            start_date: course.start_date,
            end_date: course.end_date,
            workload_hours: course.workload_hours,
          });
        }
      }

      // Handle languages
      const existingLangIds = languages.filter((l) => l.id).map((l) => l.id);

      await supabase
        .from("candidate_languages")
        .delete()
        .eq("candidate_id", candidateId)
        .not("id", "in", `(${existingLangIds.join(",") || "00000000-0000-0000-0000-000000000000"})`);

      for (const lang of languages) {
        if (lang.id) {
          await supabase
            .from("candidate_languages")
            .update({
              language: lang.language,
              level: lang.level,
            })
            .eq("id", lang.id);
        } else {
          await supabase.from("candidate_languages").insert({
            candidate_id: candidateId,
            language: lang.language,
            level: lang.level,
          });
        }
      }

      toast({
        title: "Sucesso!",
        description: "Seu currículo foi atualizado com sucesso.",
      });
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: "Erro ao salvar",
        description: "Ocorreu um erro ao salvar suas informações. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!candidateId) return;

    setIsDeleting(true);

    try {
      // Delete in order: application_answers -> applications -> candidate data -> profile -> user_roles
      const { data: applications } = await supabase.from("applications").select("id").eq("candidate_id", candidateId);

      if (applications && applications.length > 0) {
        const appIds = applications.map((a) => a.id);
        await supabase.from("application_answers").delete().in("application_id", appIds);
        await supabase.from("applications").delete().eq("candidate_id", candidateId);
      }

      // Delete candidate related data
      await supabase.from("candidate_experiences").delete().eq("candidate_id", candidateId);
      await supabase.from("candidate_education").delete().eq("candidate_id", candidateId);
      await supabase.from("candidate_courses").delete().eq("candidate_id", candidateId);
      await supabase.from("candidate_skills").delete().eq("candidate_id", candidateId);
      await supabase.from("candidate_languages").delete().eq("candidate_id", candidateId);

      // Delete candidate profile
      await supabase.from("candidate_profiles").delete().eq("id", candidateId);

      // Delete user role
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("user_roles").delete().eq("user_id", user.id);
      }

      // Sign out
      await supabase.auth.signOut();

      toast({
        title: "Conta excluída",
        description: "Sua conta foi excluída com sucesso.",
      });

      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast({
        title: "Erro",
        description: "Erro ao excluir conta. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        toast({
          title: "Erro",
          description: "Você precisa estar logado para gerar o currículo.",
          variant: "destructive",
        });
        return;
      }

      const response = await supabase.functions.invoke('generate-resume-pdf');

      if (response.error) {
        throw new Error(response.error.message || 'Erro ao gerar PDF');
      }

      // Create blob from response data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Open PDF in new tab
      window.open(url, '_blank');

      toast({
        title: "Sucesso!",
        description: "Seu currículo foi gerado com sucesso.",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Erro ao gerar currículo",
        description: "Ocorreu um erro ao gerar seu currículo. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Experience handlers
  const addExperience = () => {
    setExperiences([
      ...experiences,
      {
        company_name: "",
        role: "",
        start_date: "",
        end_date: null,
        description: "",
        is_current: false,
      },
    ]);
  };

  const updateExperience = (index: number, field: keyof Experience, value: string | boolean | null) => {
    setExperiences((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeExperience = (index: number) => {
    setExperiences(experiences.filter((_, i) => i !== index));
  };

  // Education handlers
  const addEducation = () => {
    setEducation([
      ...education,
      {
        course: "",
        institution: "",
        start_date: "",
        end_date: null,
        status: "concluido",
      },
    ]);
  };

  const updateEducation = (index: number, field: keyof Education, value: string | null) => {
    const updated = [...education];
    updated[index] = { ...updated[index], [field]: value };
    // If status changed to em_curso, clear end_date
    if (field === "status" && value === "em_curso") {
      updated[index].end_date = null;
    }
    setEducation(updated);
  };

  const removeEducation = (index: number) => {
    setEducation(education.filter((_, i) => i !== index));
  };

  // Course handlers
  const addCourse = () => {
    setCourses([
      ...courses,
      {
        course_name: "",
        institution: "",
        start_date: "",
        end_date: null,
        workload_hours: null,
      },
    ]);
  };

  const updateCourse = (index: number, field: keyof Course, value: string | number | null) => {
    const updated = [...courses];
    updated[index] = { ...updated[index], [field]: value };
    setCourses(updated);
  };

  const removeCourse = (index: number) => {
    setCourses(courses.filter((_, i) => i !== index));
  };

  // Language handlers
  const addLanguage = () => {
    setLanguages([...languages, { language: "", level: "" }]);
  };

  const updateLanguage = (index: number, field: keyof Language, value: string) => {
    const updated = [...languages];
    updated[index] = { ...updated[index], [field]: value };
    setLanguages(updated);
  };

  const removeLanguage = (index: number) => {
    setLanguages(languages.filter((_, i) => i !== index));
  };

  // Skill handlers
  const addHardSkill = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newHardSkill.trim()) {
      e.preventDefault();
      if (!hardSkills.includes(newHardSkill.trim())) {
        setHardSkills([...hardSkills, newHardSkill.trim()]);
      }
      setNewHardSkill("");
    }
  };

  const removeHardSkill = (skill: string) => {
    setHardSkills(hardSkills.filter((s) => s !== skill));
  };

  const addSoftSkill = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newSoftSkill.trim()) {
      e.preventDefault();
      if (!softSkills.includes(newSoftSkill.trim())) {
        setSoftSkills([...softSkills, newSoftSkill.trim()]);
      }
      setNewSoftSkill("");
    }
  };

  const removeSoftSkill = (skill: string) => {
    setSoftSkills(softSkills.filter((s) => s !== skill));
  };

  const toggleJobType = (type: string) => {
    if (selectedJobTypes.includes(type)) {
      setSelectedJobTypes(selectedJobTypes.filter((t) => t !== type));
    } else {
      setSelectedJobTypes([...selectedJobTypes, type]);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container py-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 container py-8">
        <div className="mobile-container max-w-4xl md:mx-auto space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-display font-bold text-foreground">Minha Conta</h1>
              <p className="text-muted-foreground">Gerencie seu currículo e informações profissionais</p>
            </div>
            <Button onClick={handleGeneratePDF} disabled={isGeneratingPDF} className="hidden sm:flex gap-2">
              {isGeneratingPDF ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Imprimir Currículo
            </Button>
          </div>

          {/* Banner de boas-vindas para primeiro acesso */}
          {isFirstAccess && (
            <div className="bg-primary/10 border border-primary/30 p-4 rounded-lg">
              <h3 className="font-semibold text-primary mb-1">Bem-vindo ao JobWayX!</h3>
              <p className="text-sm text-muted-foreground">
                Complete seu currículo para começar a se candidatar às vagas e ser encontrado pelas empresas.
              </p>
            </div>
          )}

          {/* Informações Pessoais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Informações Pessoais
              </CardTitle>
              <CardDescription>Seus dados básicos de contato</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2 overflow-hidden">
              <div className="space-y-2 min-w-0">
                <Label htmlFor="full_name">Nome completo *</Label>
                <Input
                  id="full_name"
                  value={profile.full_name || ""}
                  onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                  placeholder="Seu nome completo"
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" value={userEmail} disabled className="bg-muted" />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="cpf">CPF</Label>
                <Input id="cpf" value={formatCPF(profile.cpf || "")} disabled className="bg-muted" />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="phone">Telefone (WhatsApp)</Label>
                <Input
                  id="phone"
                  value={formatPhone(profile.phone || "")}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value.replace(/\D/g, "") })}
                  placeholder="(11) 99999-9999"
                />
              </div>
              <div className="md:col-span-2 min-w-0">
                <StateCitySelector
                  state={profile.state || ""}
                  city={profile.city || ""}
                  onStateChange={(value) => setProfile((prev) => ({ ...prev, state: value, city: "" }))}
                  onCityChange={(value) => setProfile((prev) => ({ ...prev, city: value }))}
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="gender">Gênero (opcional)</Label>
                <Select
                  value={profile.gender || ""}
                  onValueChange={(value) => setProfile({ ...profile, gender: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map((gender) => (
                      <SelectItem key={gender} value={gender}>
                        {gender}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 min-w-0">
                <Label>Estado civil (opcional)</Label>
                <Select
                  value={profile.marital_status || ""}
                  onValueChange={(value) => setProfile({ ...profile, marital_status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {MARITAL_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0">
                <DateSelector
                  label="Data de nascimento (opcional)"
                  value={profile.birth_date || null}
                  onChange={(date) => setProfile({ ...profile, birth_date: date })}
                            minYear={1920}
                            maxYear={2026}
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label>Possui filhos menores de 18 anos?</Label>
                <RadioGroup
                  value={
                    profile.has_children_under_18 === true
                      ? "sim"
                      : profile.has_children_under_18 === false
                        ? "nao"
                        : ""
                  }
                  onValueChange={(value) => setProfile({ ...profile, has_children_under_18: value === "sim" })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sim" id="children-yes" />
                    <Label htmlFor="children-yes" className="font-normal">
                      Sim
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="nao" id="children-no" />
                    <Label htmlFor="children-no" className="font-normal">
                      Não
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              <div className="space-y-2 min-w-0 md:col-span-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="bio">Sobre você</Label>
                  <span className="text-xs text-muted-foreground">{(profile.bio || "").length}/2000</span>
                </div>
                <Textarea
                  id="bio"
                  value={profile.bio || ""}
                  onChange={(e) => {
                    if (e.target.value.length <= 2000) {
                      setProfile({ ...profile, bio: e.target.value });
                    }
                  }}
                  placeholder="Conte um pouco sobre você, sua experiência e objetivos..."
                  rows={4}
                  maxLength={2000}
                />
              </div>
            </CardContent>
          </Card>

          {/* Links e Presença Online */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="h-5 w-5 text-primary" />
                Links e Presença Online
              </CardTitle>
              <CardDescription>Seus perfis e portfólios profissionais</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="linkedin_url">LinkedIn</Label>
                <Input
                  id="linkedin_url"
                  type="url"
                  value={profile.linkedin_url || ""}
                  onChange={(e) => setProfile({ ...profile, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/seu-perfil"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="github_url">Instagram (opcional)</Label>
                <Input
                  id="github_url"
                  type="url"
                  value={profile.github_url || ""}
                  onChange={(e) => setProfile({ ...profile, github_url: e.target.value })}
                  placeholder="https://instagram.com/seu-usuario"
                />
              </div>
            </CardContent>
          </Card>

          {/* Objetivo Profissional */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Objetivo Profissional
              </CardTitle>
              <CardDescription>O que você está buscando</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="desired_area">Área desejada</Label>
                <Select
                  value={profile.desired_area || ""}
                  onValueChange={(value) => setProfile({ ...profile, desired_area: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a área" />
                  </SelectTrigger>
                  <SelectContent>
                    {AREAS.map((area) => (
                      <SelectItem key={area} value={area}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
              </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Cargos pretendidos (até 3)</Label>
                  <span className="text-sm text-muted-foreground">{desiredPositions.length}/3</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {desiredPositions.map((position, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {position}
                      <button
                        type="button"
                        onClick={() => setDesiredPositions(desiredPositions.filter((_, i) => i !== index))}
                        className="ml-1 hover:text-destructive"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
                <Input
                  placeholder={desiredPositions.length >= 3 ? "Limite de 3 cargos atingido" : "Digite o cargo e pressione Enter"}
                  value={newPosition}
                  onChange={(e) => setNewPosition(e.target.value)}
                  disabled={desiredPositions.length >= 3}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const trimmed = newPosition.trim();
                      if (trimmed && desiredPositions.length < 3 && !desiredPositions.includes(trimmed)) {
                        setDesiredPositions([...desiredPositions, trimmed]);
                        setNewPosition("");
                      } else if (desiredPositions.length >= 3) {
                        toast({
                          title: "Limite atingido",
                          description: "Você pode adicionar no máximo 3 cargos pretendidos.",
                          variant: "destructive"
                        });
                      }
                    }
                  }}
                />
              </div>

              <div className="space-y-3">
                <Label>Tipo de vaga</Label>
                <div className="flex flex-wrap gap-3">
                  {JOB_TYPES.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`job-type-${type}`}
                        checked={selectedJobTypes.includes(type)}
                        onCheckedChange={() => toggleJobType(type)}
                      />
                      <label
                        htmlFor={`job-type-${type}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {type}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Modelo de trabalho</Label>
                <div className="flex flex-wrap gap-4">
                  {WORK_MODELS.map((model) => (
                    <div key={model} className="flex items-center space-x-2">
                      <Checkbox
                        id={`work-model-${model}`}
                        checked={selectedWorkModels.includes(model)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedWorkModels([...selectedWorkModels, model]);
                          } else {
                            setSelectedWorkModels(selectedWorkModels.filter((m) => m !== model));
                          }
                        }}
                      />
                      <label
                        htmlFor={`work-model-${model}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {model}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Experiências Profissionais */}
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-primary" />
                  Experiências Profissionais
                </CardTitle>
                <CardDescription>Suas experiências de trabalho anteriores</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={addExperience} className="gap-2 w-full sm:w-auto">
                <Plus className="h-4 w-4" /> Adicionar
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {experiences.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Nenhuma experiência adicionada. Clique em "Adicionar" para incluir.
                </p>
              ) : (
                experiences.map((exp, index) => (
                  <div key={index} className="border rounded-lg p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Experiência {index + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive flex-shrink-0"
                        onClick={() => removeExperience(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid gap-3 sm:gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Empresa</Label>
                        <Input
                          value={exp.company_name}
                          onChange={(e) => updateExperience(index, "company_name", e.target.value)}
                          placeholder="Nome da empresa"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cargo</Label>
                        <Input
                          value={exp.role}
                          onChange={(e) => updateExperience(index, "role", e.target.value)}
                          placeholder="Seu cargo"
                        />
                      </div>
                      <DateSelector
                        label="Data de início"
                        value={exp.start_date || null}
                        onChange={(date) => updateExperience(index, "start_date", date || "")}
                        minYear={1970}
                        hideDay
                      />
                      <div className="space-y-2">
                        <DateSelector
                          label="Data de término"
                          value={exp.end_date}
                          onChange={(date) => updateExperience(index, "end_date", date)}
                          minYear={1970}
                          disabled={exp.is_current}
                          hideDay
                        />
                        <div className="flex items-center gap-2 pt-2">
                          <Checkbox
                            id={`exp-current-${index}`}
                            checked={exp.is_current === true}
                            onCheckedChange={(checked) => {
                              setExperiences((prev) => {
                                const updated = [...prev];
                                const isChecked = checked === true;
                                updated[index] = {
                                  ...updated[index],
                                  is_current: isChecked,
                                  end_date: isChecked ? null : updated[index].end_date,
                                };
                                return updated;
                              });
                            }}
                            className="cursor-pointer"
                          />
                          <Label
                            htmlFor={`exp-current-${index}`}
                            className="text-sm text-muted-foreground cursor-pointer font-normal"
                          >
                            Trabalho atual
                          </Label>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Descrição das atividades</Label>
                      <Textarea
                        value={exp.description}
                        onChange={(e) => updateExperience(index, "description", e.target.value)}
                        placeholder="Descreva suas principais atividades e conquistas..."
                        rows={3}
                      />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Escolaridade */}
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-primary" />
                  Escolaridade
                </CardTitle>
                <CardDescription>Sua formação acadêmica</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={addEducation} className="gap-2 w-full sm:w-auto">
                <Plus className="h-4 w-4" /> Adicionar
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {education.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Nenhuma escolaridade adicionada. Clique em "Adicionar" para incluir.
                </p>
              ) : (
                education.map((edu, index) => (
                  <div key={index} className="border rounded-lg p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Formação {index + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive flex-shrink-0"
                        onClick={() => removeEducation(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid gap-3 sm:gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Curso</Label>
                        <Input
                          value={edu.course}
                          onChange={(e) => updateEducation(index, "course", e.target.value)}
                          placeholder="Nome do curso"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Instituição</Label>
                        <Input
                          value={edu.institution}
                          onChange={(e) => updateEducation(index, "institution", e.target.value)}
                          placeholder="Nome da instituição"
                        />
                      </div>
                      <DateSelector
                        label="Data de início"
                        value={edu.start_date || null}
                        onChange={(date) => updateEducation(index, "start_date", date || "")}
                        minYear={1970}
                        hideDay
                      />
                      <DateSelector
                        label="Data de conclusão"
                        value={edu.end_date}
                        onChange={(date) => updateEducation(index, "end_date", date)}
                        minYear={1970}
                        disabled={edu.status === "em_curso"}
                        hideDay
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <div className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap sm:gap-2">
                        {EDUCATION_STATUSES.map((status) => (
                          <Button
                            key={status.value}
                            type="button"
                            variant={edu.status === status.value ? "default" : "outline"}
                            size="sm"
                            onClick={() => updateEducation(index, "status", status.value)}
                          >
                            {status.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Cursos */}
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  Cursos
                </CardTitle>
                <CardDescription>Cursos complementares e capacitações</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={addCourse} className="gap-2 w-full sm:w-auto">
                <Plus className="h-4 w-4" /> Adicionar
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {courses.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Nenhum curso adicionado. Clique em "Adicionar" para incluir.
                </p>
              ) : (
                courses.map((course, index) => (
                  <div key={index} className="border rounded-lg p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Curso {index + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive flex-shrink-0"
                        onClick={() => removeCourse(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {/* Nome e Instituição */}
                    <div className="grid gap-3 sm:gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Nome do curso</Label>
                        <Input
                          value={course.course_name}
                          onChange={(e) => updateCourse(index, "course_name", e.target.value)}
                          placeholder="Nome do curso"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Instituição</Label>
                        <Input
                          value={course.institution}
                          onChange={(e) => updateCourse(index, "institution", e.target.value)}
                          placeholder="Nome da instituição"
                        />
                      </div>
                    </div>
                    {/* Datas e carga horária */}
                    <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3">
                      <DateSelector
                        label="Data de início"
                        value={course.start_date || null}
                        onChange={(date) => updateCourse(index, "start_date", date || "")}
                        minYear={1990}
                        hideDay
                      />
                      <DateSelector
                        label="Data de conclusão"
                        value={course.end_date}
                        onChange={(date) => updateCourse(index, "end_date", date)}
                        minYear={1990}
                        hideDay
                      />
                      <div className="space-y-2 col-span-2 sm:col-span-1">
                        <Label>Carga horária (h)</Label>
                        <Input
                          type="number"
                          value={course.workload_hours || ""}
                          onChange={(e) =>
                            updateCourse(index, "workload_hours", e.target.value ? parseInt(e.target.value) : null)
                          }
                          placeholder="Ex: 40"
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Competências */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Competências
              </CardTitle>
              <CardDescription>Suas competências técnicas e comportamentais</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6">
              <div className="space-y-2 sm:space-y-3">
                <Label className="text-sm leading-relaxed">Competências técnicas (ex: informática, softwares, máquinas)</Label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2">
                  {hardSkills.map((skill) => (
                    <Badge key={skill} variant="secondary" className="gap-1 text-xs sm:text-sm py-1 px-2 sm:py-1.5 sm:px-2.5">
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeHardSkill(skill)}
                        className="ml-1 hover:text-destructive"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
                <Input
                  value={newHardSkill}
                  onChange={(e) => setNewHardSkill(e.target.value)}
                  onKeyDown={addHardSkill}
                  placeholder="Digite e pressione Enter"
                  className="text-sm"
                />
              </div>

              <div className="space-y-2 sm:space-y-3">
                <Label className="text-sm leading-relaxed">Competências comportamentais (ex: comunicação, trabalho em equipe) - opcional</Label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2">
                  {softSkills.map((skill) => (
                    <Badge key={skill} variant="outline" className="gap-1 text-xs sm:text-sm py-1 px-2 sm:py-1.5 sm:px-2.5">
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeSoftSkill(skill)}
                        className="ml-1 hover:text-destructive"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
                <Input
                  value={newSoftSkill}
                  onChange={(e) => setNewSoftSkill(e.target.value)}
                  onKeyDown={addSoftSkill}
                  placeholder="Digite e pressione Enter"
                  className="text-sm"
                />
              </div>
            </CardContent>
          </Card>

          {/* Idiomas */}
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-primary" />
                  Idiomas
                </CardTitle>
                <CardDescription>Idiomas que você domina</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={addLanguage} className="gap-2 w-full sm:w-auto">
                <Plus className="h-4 w-4" /> Adicionar
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {languages.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Nenhum idioma adicionado. Clique em "Adicionar" para incluir.
                </p>
              ) : (
                languages.map((lang, index) => (
                  <div key={index} className="border rounded-lg p-3 sm:p-0 sm:border-0 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
                    {/* Header mobile */}
                    <div className="flex items-center justify-between sm:hidden">
                      <span className="text-sm font-medium text-muted-foreground">
                        Idioma {index + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => removeLanguage(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <LanguageSelector
                        value={lang.language}
                        onChange={(value) => updateLanguage(index, "language", value)}
                        placeholder="Selecione o idioma"
                      />
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4">
                      <div className="flex-1 sm:w-40 sm:flex-none">
                        <Select value={lang.level} onValueChange={(value) => updateLanguage(index, "level", value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Nível" />
                          </SelectTrigger>
                          <SelectContent>
                            {LANGUAGE_LEVELS.map((level) => (
                              <SelectItem key={level} value={level}>
                                {level}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Botão deletar desktop */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="hidden sm:flex h-8 w-8 text-destructive hover:text-destructive flex-shrink-0"
                        onClick={() => removeLanguage(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Dados Adicionais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5 text-primary" />
                Dados Adicionais
              </CardTitle>
              <CardDescription>Informações complementares (opcional)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="availability">Disponibilidade para início</Label>
                  <Input
                    id="availability"
                    value={profile.availability || ""}
                    onChange={(e) => setProfile({ ...profile, availability: e.target.value })}
                    placeholder="Ex: Imediata, 15 dias, 30 dias..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="salary_min">Pretensão salarial (mínimo)</Label>
                  <Input
                    id="salary_min"
                    type="text"
                    value={formatCurrency(profile.salary_expectation_min)}
                    onChange={(e) => {
                      const formatted = handleCurrencyInput(e.target.value);
                      setProfile({
                        ...profile,
                        salary_expectation_min: parseCurrency(formatted),
                      });
                    }}
                    placeholder="R$ 0,00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="salary_max">Pretensão salarial (máximo)</Label>
                  <Input
                    id="salary_max"
                    type="text"
                    value={formatCurrency(profile.salary_expectation_max)}
                    onChange={(e) => {
                      const formatted = handleCurrencyInput(e.target.value);
                      setProfile({
                        ...profile,
                        salary_expectation_max: parseCurrency(formatted),
                      });
                    }}
                    placeholder="R$ 0,00"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="is_pcd"
                  checked={profile.is_pcd || false}
                  onCheckedChange={(checked) => setProfile({ ...profile, is_pcd: !!checked })}
                />
                <label
                  htmlFor="is_pcd"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Sou Pessoa com Deficiência (PCD)
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Botões */}
          <div className="flex flex-col-reverse gap-4 sm:flex-row sm:justify-between sm:items-center">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full sm:w-auto gap-2" disabled={isDeleting}>
                  {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Excluir Minha Conta
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Tem certeza que deseja excluir sua conta?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação é irreversível. Todos os seus dados, candidaturas, experiências e informações serão
                    permanentemente deletados.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive hover:bg-destructive/90">
                    Sim, excluir minha conta
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Button 
              onClick={handleGeneratePDF} 
              disabled={isGeneratingPDF} 
              variant="outline"
              className="w-full sm:hidden gap-2"
            >
              {isGeneratingPDF ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Imprimir Currículo
            </Button>

            <Button onClick={handleSave} disabled={isSaving} size="lg" className="w-full sm:w-auto gap-2">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salvar Alterações
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
